/** @jsxImportSource theme-ui */
import React from 'react';
import { Box, Flex, Heading, Text } from 'theme-ui';
import Button from '@/components/Button';
import { ArticlesExample, EmbedLevel, PricesExample } from '@/containers/widget/embedScript';
import { useCopyFeedback } from './useCopyFeedback';

interface Props {
    level: EmbedLevel;
    onLevelChange: (level: EmbedLevel) => void;
    articlesExample: ArticlesExample;
    onArticlesExampleChange: (example: ArticlesExample) => void;
    pricesExample: PricesExample;
    onPricesExampleChange: (example: PricesExample) => void;
    snippet: string;
    isCopyDisabled: boolean;
}

interface Tab<T extends string> {
    value: T;
    label: string;
    description: string;
}

const LEVEL_TABS: Tab<EmbedLevel>[] = [
    { value: 'basic', label: 'Basic', description: 'Loads the widget by configuration id.' },
    {
        value: 'articles',
        label: 'Articles',
        description:
            'Reacts to the visitor’s add-on selection through selectedArticlesChange and setArticleQuantity.',
    },
    {
        value: 'customer',
        label: 'Customer',
        description:
            'Prefills the booking form. Must be pushed after config; the SDK holds it until the widget is ready and ignores it for logged-in visitors.',
    },
    {
        value: 'prices',
        label: 'Prices',
        description:
            'Reacts to the visitor’s price and spot selection through priceQuantitiesChange and setPriceQuantity.',
    },
];

export const ARTICLES_EXAMPLE_TABS: Tab<ArticlesExample>[] = [
    {
        value: 'helper',
        label: 'Event log + helper',
        description:
            'Logs every selectedArticlesChange snapshot and defines bokameraSetArticleQuantity. Call it after onReady and after the visitor has selected a service.',
    },
    {
        value: 'synced',
        label: 'Synced add-ons',
        description:
            'Keeps a group of checkout add-ons at the same quantity: when one changes (in the widget or via a host command) the page sets the others to match. Only type 6 add-ons with Duration 0 take part.',
    },
];

export const PRICES_EXAMPLE_TABS: Tab<PricesExample>[] = [
    {
        value: 'helper',
        label: 'Event log + helper',
        description:
            'Logs every priceQuantitiesChange snapshot and defines bokameraSetPriceQuantity. Call it after the visitor has picked a time; price ids can change when the time is chosen.',
    },
    {
        value: 'synced',
        label: 'Synced prices',
        description:
            'Keeps a group of price counters at the same quantity: when one changes (in the widget or via a host command) the page sets the others to match. Counter-mode services only — two or more prices plus a spot range; the widget rejects a follow-up over the service maximum or the spots left.',
    },
];

const EmbedCodeColumn: React.FC<Props> = ({
    level,
    onLevelChange,
    articlesExample,
    onArticlesExampleChange,
    pricesExample,
    onPricesExampleChange,
    snippet,
    isCopyDisabled,
}) => {
    const { isCopied, copy } = useCopyFeedback();

    return (
        <Box>
            <Flex sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Heading as="h4" sx={{ m: 0 }}>
                    Embed code
                </Heading>
                <Button
                    type="button"
                    variant="secondary"
                    disabled={isCopyDisabled}
                    onClick={() => copy(snippet)}
                >
                    {isCopied ? 'Copied!' : 'Copy'}
                </Button>
            </Flex>
            <TabRow tabs={LEVEL_TABS} active={level} onChange={onLevelChange} />
            {level === 'articles' && (
                <TabRow
                    tabs={ARTICLES_EXAMPLE_TABS}
                    active={articlesExample}
                    onChange={onArticlesExampleChange}
                    ariaLabel="Articles example"
                />
            )}
            {level === 'prices' && (
                <TabRow
                    tabs={PRICES_EXAMPLE_TABS}
                    active={pricesExample}
                    onChange={onPricesExampleChange}
                    ariaLabel="Prices example"
                />
            )}
            <Box
                as="pre"
                data-testid="embed-snippet"
                sx={{
                    fontFamily: 'monospace',
                    fontSize: 1,
                    backgroundColor: 'gray.2',
                    borderRadius: 'sm',
                    p: 2,
                    overflowX: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                }}
            >
                {snippet}
            </Box>
        </Box>
    );
};

function TabRow<T extends string>({
    tabs,
    active,
    onChange,
    ariaLabel,
}: {
    tabs: Tab<T>[];
    active: T;
    onChange: (value: T) => void;
    ariaLabel?: string;
}) {
    const activeTab = tabs.find((tab) => tab.value === active) ?? tabs[0];

    return (
        <Box sx={{ mb: 2 }}>
            <Flex sx={{ gap: 2, mb: 2, flexWrap: 'wrap' }} role="group" aria-label={ariaLabel}>
                {tabs.map((tab) => (
                    <Button
                        key={tab.value}
                        type="button"
                        variant={tab.value === active ? 'primary' : 'secondary'}
                        aria-pressed={tab.value === active}
                        onClick={() => onChange(tab.value)}
                    >
                        {tab.label}
                    </Button>
                ))}
            </Flex>
            <Text sx={{ display: 'block', fontSize: 1, color: 'gray.6' }}>
                {activeTab.description}
            </Text>
        </Box>
    );
}

export default EmbedCodeColumn;
